import { editUserDetails, getOrgUsers } from '@api/user';
import { AccessControl } from '@auth/accessControl';
import toast from '@components/Common/Toast.js';
import ProjectHeader from '@components/Projects/header/ProjectHeader.js';
import ProjectCreateModal from '@components/Projects/Modals/ProjectCreateModal.js';
import ProjectContainer from '@components/Projects/ProjectContainer.js';
import ProjectGridSkeleton from '@components/Projects/skeleton/ProjectGridSkeleton.js';
import ProjectHeaderSkeleton from '@components/Projects/skeleton/ProjectHeaderSkeleton.js';
import ProjectListSkeleton from '@components/Projects/skeleton/ProjectListSkeleton.js';
import BasePage from '@pages/BasePage';
import { projectListUpdated } from '@signals/CommonSignals.js';
import OrganizationState from '@states/Organization';
import ProjectsState, {
  CurrentFolderId,
  CurrentItemsState,
  LoadedFolderIds,
} from '@states/Projects';
import UserState from '@states/User';
import { MemberState } from '@states/Viewer';
import {
  getFolderIdFromUrl,
  getSharedProjectId,
  getSharingId,
} from '@utils/projectUtils';

/**
 * Projects page that handles displaying and managing projects
 * Supports personal, team, and shared project types
 */
export default class ProjectsPage extends BasePage {
  constructor() {
    super();
    this.setId('projects-page');
    this.viewMode = 'list'; // for now we default to list view until grid view UI changes are done
    // localStorage.getItem('projectViewMode') === 'grid' ? 'grid' : 'list';
    this.currentFolderId = null;
    this.selectedOrgChanged = false; // Track if the organization has changed
    this.isErrorView = false; // Track if we are showing an error view due to subscription issues
    this.isInitializing = false; // Prevent double initialization
    this.isInitialized = false; // Track if page has been initialized
    this.isLoadingProjects = false; // Prevent concurrent project loading
    this.isHeaderLoaded = false; // Track header loading state
    this.isContainerLoaded = false; // Track container loading state
    this.addClass('h-full flex flex-col w-full p-2 md:p-4');
    this.setStyle('position', ['relative']); // Enable absolute positioning for loader

    this.unsubscribeUserState = UserState.subscribe(
      this.handleUserStateChange.bind(this),
    );

    this.unsubscribeProjectState = ProjectsState.subscribe(projects => {
      const isFetchedProjectsSubError = !!projects.subscriptionError;

      const needsRerender =
        !this.selectedOrgChanged &&
        this.isErrorView !== isFetchedProjectsSubError;

      if (needsRerender) {
        this.isErrorView = isFetchedProjectsSubError;
        this.initializeComponents();
      }

      // Always reset after processing
      this.selectedOrgChanged = false;
    });

    this.unsubscribeProjectListUpdated = projectListUpdated.on(() => {
      this.onLoadProjects(false, true);
    });

    this.initialize();
  }

  /**
   * Show skeleton loaders immediately for a better UX
   * @param {string|null} folderId - Optional folder ID to show folder-specific skeleton
   */
  #showSkeletons(folderId = null) {
    this.clear();

    // Create and add header skeleton
    this.headerSkeleton = new ProjectHeaderSkeleton();
    this.add(this.headerSkeleton);

    // Create and add content skeleton based on view mode
    if (this.viewMode === 'grid') {
      this.containerSkeleton = new ProjectGridSkeleton({ itemCount: 8 });
    } else {
      // Pass folderId to show breadcrumb and add task row if navigating to a folder
      this.containerSkeleton = new ProjectListSkeleton({
        rowCount: 8,
        folderId,
        canCreate: true, // We assume user can create until we know otherwise
      });
    }
    this.containerSkeleton.addClass('flex-1');
    this.add(this.containerSkeleton);
  }

  /**
   * Replace header skeleton with real header
   */
  #replaceHeaderSkeleton() {
    if (this.headerSkeleton && this.header) {
      // Find the index of the skeleton
      const children = Array.from(this.dom.children);
      const skeletonIndex = children.indexOf(this.headerSkeleton.dom);

      if (skeletonIndex !== -1) {
        // Remove skeleton and insert real header at the same position
        this.headerSkeleton.dom.remove();
        if (skeletonIndex === 0) {
          this.dom.insertBefore(this.header.dom, this.dom.firstChild);
        } else {
          this.dom.insertBefore(this.header.dom, children[skeletonIndex]);
        }
      }
      this.headerSkeleton = null;
    }
  }

  /**
   * Replace container skeleton with real container
   */
  #replaceContainerSkeleton() {
    if (this.containerSkeleton && this.projectViewContainer) {
      // Find the index of the skeleton
      const children = Array.from(this.dom.children);
      const skeletonIndex = children.indexOf(this.containerSkeleton.dom);

      if (skeletonIndex !== -1) {
        // Remove skeleton and insert real container at the same position
        this.containerSkeleton.dom.remove();
        this.dom.appendChild(this.projectViewContainer.dom);
      }
      this.containerSkeleton = null;
    }
  }

  async initialize(forceReload = false) {
    // Prevent double initialization
    if (this.isInitializing) return;

    this.isInitializing = true;
    this.isHeaderLoaded = false;
    this.isContainerLoaded = false;

    if (forceReload) this.clear();

    // Check if there's a folder ID in the URL to navigate directly
    this.directFolderId = getFolderIdFromUrl();

    // Show skeletons immediately for better perceived performance
    // Pass folderId to show folder-specific skeleton with breadcrumb
    this.#showSkeletons(this.directFolderId);

    try {
      // Initialize basic page state (sync operations)
      this.#initializeBasicState();

      // If we have a direct folder ID, load that folder directly while loading all projects in background
      if (this.directFolderId) {
        this.#loadDirectFolderAsync(forceReload);
      } else {
        // Start loading header and projects in parallel but independently
        this.#loadHeaderAsync();
        this.#loadContainerAsync(forceReload);
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing projects page:', error);
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Initialize basic page state synchronously
   */
  #initializeBasicState() {
    const isLoggedIn = !!UserState.user;
    // Only subscribe to UserState if user is logged in
    this.selectedOrg = isLoggedIn ? UserState.user.selectedOrg : null;

    const sharedProjectId = getSharedProjectId();

    if (sharedProjectId) {
      this.selectedProjectType = 'shared';
      this.isAnonymousView = !isLoggedIn;
    } else {
      // Check if there's a localStorage override (e.g., from invitation flow)
      const localStorageProjectType = localStorage.getItem(
        'selectedProjectType',
      );
      this.selectedProjectType =
        localStorageProjectType ||
        UserState.user?.selectedProjectsType ||
        'personal';

      // Clear the localStorage override after using it
      if (localStorageProjectType)
        localStorage.removeItem('selectedProjectType');
    }

    // Setup access control (needed for header)
    this.setupAccessControl();
  }

  /**
   * Load header asynchronously and replace skeleton when ready
   */
  async #loadHeaderAsync() {
    try {
      // If team project, we need org users for header
      if (this.selectedProjectType === 'team') {
        await this.loadCurrentOrgUsers();
      }

      // Create the header
      this.#createHeader();

      // Replace skeleton with real header
      this.#replaceHeaderSkeleton();
      this.isHeaderLoaded = true;
    } catch (error) {
      console.error('Error loading header:', error);
      // Still try to show header even on error
      this.#createHeader();
      this.#replaceHeaderSkeleton();
      this.isHeaderLoaded = true;
    }
  }

  /**
   * Load container (projects) asynchronously and replace skeleton when ready
   */
  async #loadContainerAsync(forceReload = false) {
    try {
      // Load projects
      await this.onLoadProjects(false, forceReload);

      // Create the project container
      this.#createProjectContainer();

      // Replace skeleton with real container
      this.#replaceContainerSkeleton();
      this.isContainerLoaded = true;

      // Setup navigation after container is loaded
      this.#setupNavigation();
    } catch (error) {
      console.error('Error loading container:', error);
      // Still try to show container even on error
      this.#createProjectContainer();
      this.#replaceContainerSkeleton();
      this.isContainerLoaded = true;
    }
  }

  /**
   * Load directly into a folder when URL has id param
   * Loads the folder content first, then loads all projects in background
   */
  async #loadDirectFolderAsync(forceReload = false) {
    try {
      // Set the current folder ID immediately
      this.currentFolderId = this.directFolderId;
      CurrentFolderId.setCurrentFolderId(this.directFolderId);

      // Start loading header in parallel (doesn't depend on folder content)
      this.#loadHeaderAsync();

      // Load the specific folder data first (this is what the user wants to see)
      const folderResult = await ProjectsState.getProjectById(
        this.directFolderId,
      );

      if (folderResult.success) {
        // Create the project container with the folder ID already set
        // The ListView will be initialized with currentFolderId and render tasks directly
        this.#createProjectContainer();

        // Replace skeleton with real container
        this.#replaceContainerSkeleton();
        this.isContainerLoaded = true;

        // Setup navigation events for future folder navigation
        this.#setupNavigationEvents();

        // Update header with current folder context
        if (this.header && this.header.updateColumnFilter) {
          this.header.updateColumnFilter(this.directFolderId);
        }

        // Load all projects in background (non-blocking)
        this.#loadAllProjectsInBackground(forceReload);
      } else {
        // If folder load fails, fall back to normal loading
        console.warn(
          'Direct folder load failed, falling back to normal loading',
        );
        await this.#loadContainerAsync(forceReload);
      }
    } catch (error) {
      console.error('Error loading direct folder:', error);
      // Fall back to normal loading on error
      await this.#loadContainerAsync(forceReload);
    }
  }

  /**
   * Load all projects in background without blocking the UI
   * Uses refresh=true to force loading even if a single project was already loaded
   */
  async #loadAllProjectsInBackground() {
    try {
      // Force refresh to load all projects even if hasBeenLoaded is true
      // (which happens when getProjectById sets it after loading a single folder)
      await this.onLoadProjects(false, true);
    } catch (error) {
      console.error('Error loading projects in background:', error);
    }
  }

  /**
   * Setup navigation event listeners only (without auto-navigation)
   */
  #setupNavigationEvents() {
    this.handleFolderNavigation = this.handleFolderNavigation.bind(this);
    this.dom.addEventListener('folderNavigated', this.handleFolderNavigation);
  }

  /**
   * Create the project header component
   */
  #createHeader() {
    this.header = new ProjectHeader({
      projectType: this.selectedProjectType,
      handleProjectTypeChange: async type => {
        // we need to check if the user is allowed to change to team project - if there is no org then it cant be possible
        if (type === 'team' && OrganizationState.organizations.length === 0) {
          toast.warning(i18next.t('toast.changeTypeTeamError'));
          return 'error';
        }

        this.selectedProjectType = type;
        this.header.setProjectType(type); // Update header project type

        const promises = [];

        // Handle user details update if needed
        if (
          type !== UserState.user?.selectedProjectsType &&
          type !== 'shared'
        ) {
          promises.push(
            editUserDetails({ selectedProjectsType: type }).then(() => {
              UserState.setUser({
                ...UserState.user,
                selectedProjectsType: type,
              });
            }),
          );
        }

        // get the current organization users if team project type is selected
        if (type === 'team') {
          promises.push(this.loadCurrentOrgUsers());
        }

        // Reset the current folder ID to null
        this.currentFolderId = null;

        // Clear loaded folder IDs cache when switching project types
        // This ensures fresh data is fetched for the new project type
        LoadedFolderIds.setLoadedFolderIds([]);
        CurrentItemsState.setCurrentItems([]);

        // reset access control
        this.setupAccessControl();

        // Load projects
        promises.push(this.onLoadProjects(true));

        await Promise.all(promises);

        // Update access control for components that need it
        this.header.setAccessControl(this.accessControl);
        this.projectViewContainer.updateAccessControl(this.accessControl);

        // Update selectedProjectType in the project view container
        this.projectViewContainer.selectedProjectType =
          this.selectedProjectType;

        // Force re-initialization of components with new access control
        this.header.render();
        this.projectViewContainer.initializeComponents();

        // Update header column filter
        if (
          this.header &&
          this.header.updateColumnFilter &&
          !ProjectsState.projects.subscriptionError
        ) {
          this.header.updateColumnFilter(null, true);
        }
      },
      onAddProject: () => {
        // Handle adding a new project
        const modal = new ProjectCreateModal();
        modal.show();
      },
      onSearch: query => {
        // Handle search functionality by forwarding to project container
        if (
          this.projectViewContainer &&
          this.projectViewContainer.handleSearch
        ) {
          this.projectViewContainer.handleSearch(query);
        }
      },
      viewMode: this.viewMode,
      onViewModeChange: async mode => {
        this.viewMode = mode;

        // remove id param from url when view mode changes
        const cleanSearch = window.location.search.replace(/\s+/g, '');
        const params = new URLSearchParams(cleanSearch);
        params.delete('pId');
        const newSearch = params.toString();

        const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
        window.history.replaceState({}, '', newUrl);

        localStorage.setItem('projectViewMode', mode);
        this.projectViewContainer.viewMode = mode;

        // When switching to grid view, we need to load all projects and tasks if not already loaded
        if (mode === 'grid') {
          const needsLoading = this.#checkIfNeedsFullLoad();

          if (needsLoading) {
            // Show grid skeleton while loading
            this.#showGridSkeletonOnly();

            const orgId =
              this.selectedProjectType === 'team' ? this.selectedOrg : null;
            await ProjectsState.getProjectsAndTasks(orgId);

            // Replace skeleton with real container
            this.#hideGridSkeleton();
          }
        }

        const sharedProjectId = getSharedProjectId();
        if (sharedProjectId) this.#handleProjectIdNavigation(sharedProjectId);
        this.projectViewContainer.initializeComponents();
      },
      isAnonymous: this.isAnonymousView,
      accessControl: this.accessControl,
      currentFolderId: this.currentFolderId,
    });
  }

  /**
   * Create the project container component
   */
  #createProjectContainer() {
    this.projectViewContainer = new ProjectContainer({
      viewMode: this.viewMode,
      accessControl: this.accessControl,
      selectedProjectType: this.selectedProjectType,
      currentFolderId: this.currentFolderId,
      isAnonymousView: this.isAnonymousView,
    });
  }

  /**
   * Check if we need to load all projects and tasks
   * Returns true if not all folder tasks have been loaded yet
   */
  #checkIfNeedsFullLoad() {
    const { folders } = ProjectsState.projects;
    const loadedIds = LoadedFolderIds.loadedFolderIds;

    // If no folders, we don't need to load
    if (!folders || folders.length === 0) return false;

    // Check if all folders have their tasks loaded
    const allFolderIds = folders.map(f => f.id);
    const allLoaded = allFolderIds.every(id => loadedIds.includes(id));

    return !allLoaded;
  }

  /**
   * Show only the grid skeleton (keeps header visible)
   */
  #showGridSkeletonOnly() {
    // Remove existing container if present
    if (this.projectViewContainer) {
      this.projectViewContainer.dom.remove();
    }

    // Create and add grid skeleton
    this.containerSkeleton = new ProjectGridSkeleton({ itemCount: 8 });
    this.containerSkeleton.addClass('flex-1');
    this.add(this.containerSkeleton);
  }

  /**
   * Hide grid skeleton and restore container
   */
  #hideGridSkeleton() {
    if (this.containerSkeleton) {
      this.containerSkeleton.dom.remove();
      this.containerSkeleton = null;
    }

    // Recreate the project container with updated data
    this.#createProjectContainer();
    this.add(this.projectViewContainer);
  }

  /**
   * Setup navigation after components are loaded
   */
  #setupNavigation() {
    // Listen for folder navigation events
    this.handleFolderNavigation = this.handleFolderNavigation.bind(this);
    this.dom.addEventListener('folderNavigated', this.handleFolderNavigation);

    // check if we need to navigate to a specific folder (e.g., we are on viewer page)
    const sharingId = getSharingId();
    const sharedProjectId = getSharedProjectId();

    if (sharingId) {
      // look into each tasks to find the sharingId and get parentId which is the folderId
      const taskToMove = ProjectsState.projects.tasks.find(
        task => task.sharingId === sharingId,
      );
      if (!taskToMove) return;
      const sourceFolderId = taskToMove.parentId;
      // navigate to the folder based on the sharing ID
      this.handleFolderNavigation({ detail: { folderId: sourceFolderId } });

      // only do this when we are not in shared project type
      if (this.selectedProjectType !== 'shared') {
        setTimeout(() => {
          this.triggerNavigateToFolder(sourceFolderId);
        }, 100);
      }
    }
    if (sharedProjectId) this.#handleProjectIdNavigation(sharedProjectId);
  }

  #handleProjectIdNavigation(sharedProjectId) {
    let { id } = ProjectsState.projects.folders.find(
      project => project.shortSharingId === sharedProjectId,
    );

    // little hack but if projectToNavigate is not found it means the shared project is owned by the user so it is not in the folders list - in that case we just take the subfolderTasks parentId
    if (!id) {
      const taskInProject = ProjectsState.projects.tasks.find(
        task => task.shortSharingId === sharedProjectId,
      );
      if (taskInProject) {
        const folderId = taskInProject.parentId;
        id = ProjectsState.projects.folders.find(
          folder => folder.id === folderId,
        );
      }
    }

    this.handleFolderNavigation({
      detail: { folderId: id },
    });

    // only do this when we are not in shared project type
    requestAnimationFrame(() => {
      this.triggerNavigateToFolder(id);
    });
  }

  /**
   * Handle folder navigation events from ProjectListView
   * Updates the header column filter with the new folder context
   * @param {CustomEvent} event - The folder navigation event
   */
  handleFolderNavigation(event) {
    const { folderId } = event.detail;

    // Update the current folder ID
    this.currentFolderId = folderId;

    // update the State - TODO: will need to refactor all code to just use the state later, sry no time now to check everything working correctly after the changes
    CurrentFolderId.setCurrentFolderId(folderId);

    // Update the header column filter with the new folder context
    if (this.header && this.header.updateColumnFilter) {
      this.header.updateColumnFilter(folderId);
    }

    // Update the project view container's current folder ID if needed
    if (this.projectViewContainer) {
      this.projectViewContainer.currentFolderId = folderId;
    }

    // update the url params to include the folderId
    const cleanSearch = window.location.search.replace(/\s+/g, '');
    const params = new URLSearchParams(cleanSearch);

    if (folderId) {
      params.set('pId', folderId);
    } else {
      params.delete('pId');
    }
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    window.history.replaceState({}, '', newUrl);
  }

  /**
   * Setup access control based on project type and user permissions
   */
  setupAccessControl() {
    const isTeamProject = this.selectedProjectType === 'team';
    const isLoggedIn = !!UserState.user;

    let overrideRole = null;
    if (this.selectedProjectType === 'shared' && UserState.user) {
      overrideRole = ProjectsState.projects?.folders[0]?.accessType || 'view';
    }

    this.accessControl = new AccessControl({
      subscribeToGlobal: false,
      initialResource: this.getInitialResource(isTeamProject, isLoggedIn),
      initialResourceType: 'space',
      initialContext: {
        spaceType:
          isTeamProject || this.selectedProjectType === 'shared'
            ? 'organization'
            : 'personal',
        overrideRole,
      },
    });
  }

  /**
   * Get initial resource configuration for access control
   */
  getInitialResource(isTeamProject, isLoggedIn) {
    return {
      type: 'space',
      organizationId: isTeamProject ? this.selectedOrg : null,
      uid: isTeamProject ? null : this.getProjectUserId(isLoggedIn),
    };
  }

  async loadCurrentOrgUsers() {
    try {
      let organizationId = null;

      // Try to get organization ID from UserState first
      if (UserState?.user?.selectedOrg) {
        organizationId = UserState.user.selectedOrg;
      }
      // Fallback to getting it from organizations list
      else if (OrganizationState.organizations?.length > 0) {
        organizationId = OrganizationState.organizations[0]?.organizationId;
      }

      if (organizationId) {
        if (MemberState.members.length > 0) return MemberState.members;
        const users = await getOrgUsers(organizationId);
        MemberState.setMembers(users);
        return users;
      }
      return [];
    } catch (error) {
      const users = [];
      console.error('Error fetching organization users:', error);
      OrganizationState.setOrganizations([
        ...OrganizationState.organizations,
        users,
      ]);
      return [];
    }
  }

  /**
   * Get user ID based on project type and authentication status
   */
  getProjectUserId(isLoggedIn) {
    if (this.selectedProjectType === 'shared') {
      return ProjectsState.projects?.folders[0]?.creatorId;
    }

    return isLoggedIn ? UserState.user.uid : null;
  }

  /**
   * Load projects based on the selected project type
   */
  async onLoadProjects(isProjectTypeChange = false, refresh = false) {
    const { hasBeenLoaded } = ProjectsState.projects;
    // If no projects are loaded, try to load them. Is loading should be 'hasBeenLoaded'
    if (hasBeenLoaded && !isProjectTypeChange && !refresh) return;

    // Prevent concurrent loading
    if (this.isLoadingProjects) return;

    this.isLoadingProjects = true;

    try {
      if (this.selectedProjectType === 'shared') {
        await this.loadSharedProject();
      } else {
        await this.loadStandardProjects();
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      this.isLoadingProjects = false;
    }
  }

  /**
   * Load standard projects (personal or team)
   */
  async loadStandardProjects() {
    const orgId = this.selectedProjectType === 'team' ? this.selectedOrg : null;
    if (this.viewMode === 'list') await ProjectsState.loadProjects(orgId);
    else await ProjectsState.getProjectsAndTasks(orgId);
  }

  /**
   * Load shared project by ID
   */
  async loadSharedProject() {
    const sharedProjectId = getSharedProjectId();
    const result = await ProjectsState.getProjectByShareId(sharedProjectId);

    if (result.success && result.project) {
      this.currentFolderId = result.project.id;

      // Update access control for the specific project
      this.updateAccessControlForProject(result.project);
    }
  }

  /**
   * Update access control configuration for a specific project
   */
  updateAccessControlForProject(project) {
    const isTeamProject = this.selectedProjectType === 'team';
    let overrideRole = null;

    if (UserState.user) {
      overrideRole = project?.accessType || 'view';
    }

    this.originalProjectsOrg = this.selectedOrg;

    this.accessControl = new AccessControl({
      subscribeToGlobal: false,
      initialResource: {
        type: 'space',
        organizationId: isTeamProject ? this.selectedOrg : null,
        uid: isTeamProject
          ? null
          : this.selectedProjectType === 'shared'
            ? project?.creatorId
            : UserState.user?.uid,
      },
      initialResourceType: 'space',
      initialContext: {
        spaceType:
          isTeamProject || this.selectedProjectType === 'shared'
            ? 'organization'
            : 'personal',
        overrideRole,
      },
    });
  }

  /**
     /**
   * Trigger navigation to a specific folder in the ProjectListView
   * @param {string} folderId - The folder ID to navigate to
   */
  triggerNavigateToFolder(folderId) {
    // Get the ProjectListView instance through the project container
    const projectListView = this.projectViewContainer?.listViewContainer;

    if (projectListView && projectListView.dom) {
      // Create and dispatch the navigateToFolder event on the ProjectListView's DOM element
      const event = new CustomEvent('navigateToFolder', {
        detail: {
          folderId: folderId,
        },
        bubbles: true,
      });

      projectListView.dom.dispatchEvent(event);
    }
  }

  /**
   * Initialize page components - called when projects state changes
   * This handles re-rendering when subscription errors occur
   */
  initializeComponents() {
    // Only reinitialize if we're already loaded
    if (!this.isHeaderLoaded || !this.isContainerLoaded) return;

    // Clear and rebuild components
    this.clear();

    // Re-add header
    if (this.header) {
      this.add(this.header);
    }

    // Rebuild project container
    this.#createProjectContainer();
    this.add(this.projectViewContainer);

    // Re-setup navigation
    this.#setupNavigation();
  }

  async handleUserStateChange(user) {
    // Only handle organization changes if the page has been initialized
    // and we're not currently initializing
    if (!this.isInitialized || this.isInitializing) return;

    if (user?.selectedOrg !== this.selectedOrg) {
      this.selectedOrgChanged = true;
      await this.initialize(true);
    }
  }

  /**
   * Clean up event listeners when the page is destroyed
   */
  dispose() {
    // Remove folder navigation event listener
    if (this.dom && this.handleFolderNavigation) {
      this.dom.removeEventListener(
        'folderNavigated',
        this.handleFolderNavigation,
      );
    }

    // Clean up header column filter
    if (
      this.header &&
      this.header.columnFilter &&
      this.header.columnFilter.destroy
    ) {
      this.header.columnFilter.destroy();
    }

    // Clean up project view container
    if (this.projectViewContainer && this.projectViewContainer.destroy) {
      this.projectViewContainer.destroy();
    }

    // Clean up skeletons if they exist
    this.headerSkeleton = null;
    this.containerSkeleton = null;

    this.unsubscribeUserState?.();
    this.unsubscribeProjectState?.();
    this.unsubscribeProjectListUpdated?.();

    // Call parent dispose for additional cleanup
    super.dispose();
  }
}
